import React, { useContext, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import SongView from '../songs/view'
import SetListView from '../setlists/view'
import RehearsalFollowerBar from './follower-bar'
import { context as RehearsalContext } from '../../context/rehearsal'

const WaitingStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
  text-align: center;
`

const WaitingTitle = styled.h2`
  color: ${({ theme }) => theme.primary};
  margin-bottom: 16px;
  font-size: 1.8rem;
`

const WaitingMessage = styled.p`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 1.1rem;
  max-width: 500px;
  line-height: 1.6;
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 1.1rem;
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.error || '#d32f2f'};
  font-size: 1.1rem;
`

const ContentWrapper = styled.div`
  /* Add top padding when follower bar is visible */
  padding-top: ${(props) => props.$hasFollowerBar ? '80px' : '0'};
`

const RehearsalView = () => {
  const { rehearsalid } = useParams()
  const { rehearsal, isFollowing, joinRehearsal, loading } = useContext(RehearsalContext)
  const scrollPositionRef = useRef(0)
  const rehearsalIdRef = useRef(null)

  // Join rehearsal on mount
  useEffect(() => {
    const rehearsalId = rehearsalid

    if (rehearsalId && rehearsalId !== rehearsalIdRef.current && joinRehearsal) {
      rehearsalIdRef.current = rehearsalId
      const joinPromise = joinRehearsal(rehearsalId)
      if (joinPromise && typeof joinPromise.catch === 'function') {
        joinPromise.catch(err => {
          console.error('Failed to join rehearsal:', err)
        })
      }
    }
  }, [rehearsalid, joinRehearsal])

  // Maintain scroll position during content updates
  useEffect(() => {
    // Save scroll position before update
    const saveScrollPosition = () => {
      scrollPositionRef.current = window.scrollY
    }

    // Restore scroll position after update
    const restoreScrollPosition = () => {
      if (scrollPositionRef.current > 0) {
        window.scrollTo(0, scrollPositionRef.current)
      }
    }

    saveScrollPosition()

    // Use requestAnimationFrame to restore after render
    const rafId = window.requestAnimationFrame(restoreScrollPosition)

    return () => {
      window.cancelAnimationFrame(rafId)
    }
  }, [rehearsal?.song, rehearsal?.setlist])

  // Handle loading state
  if (loading && !rehearsal) {
    return <LoadingMessage>Loading rehearsal...</LoadingMessage>
  }

  // Handle error state - rehearsal not found or not accessible
  if (!loading && !rehearsal) {
    return (
      <ErrorMessage>
        Unable to load rehearsal. Please check that the rehearsal exists and you have access to it.
      </ErrorMessage>
    )
  }

  const { song, setlist } = rehearsal || {}

  // Convert ObjectIds to strings for component props
  const songid = song ? String(song) : null
  const setlistid = setlist ? String(setlist) : null

  // Show waiting state when no content is set
  if (!songid && !setlistid) {
    return (
      <>
        {isFollowing && <RehearsalFollowerBar />}
        <ContentWrapper $hasFollowerBar={isFollowing}>
          <WaitingStateContainer>
            <WaitingTitle>Waiting for Content</WaitingTitle>
            <WaitingMessage>
              The rehearsal owner hasn't selected any content yet.
              Your screen will automatically update when they navigate to a song or setlist.
            </WaitingMessage>
          </WaitingStateContainer>
        </ContentWrapper>
      </>
    )
  }

  return (
    <>
      {isFollowing && <RehearsalFollowerBar />}
      <ContentWrapper $hasFollowerBar={isFollowing}>
        {songid && setlistid && <SongView key={`${songid}-${setlistid}`} songid={songid} setlistid={setlistid} />}
        {songid && !setlistid && <SongView key={songid} songid={songid} />}
        {!songid && setlistid && <SetListView key={setlistid} setlistid={setlistid} />}
      </ContentWrapper>
    </>
  )
}

export default RehearsalView
